import { ApiError, type AccountQrActiveResult, type AccountQrIssueResult, type HqApi } from '@hq/api-sdk';
import { CopyOutlined, DownloadOutlined, ReloadOutlined, StopOutlined } from '@ant-design/icons';
import { formatDisplayTime } from '@hq/utils';
import { Alert, App, Button, Descriptions, Space, Spin, Tag, Typography } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { downloadDataUrl, qrDataUrl, safeFileName } from './qr';

export interface SelfQrPanelProps {
  hq: HqApi;
  /** 抽屉是否打开且停留在本页签（用于按需加载，避免每次开抽屉都请求）。 */
  active: boolean;
  userName?: string | null;
  onClose: () => void;
}

type QrData = AccountQrIssueResult | AccountQrActiveResult;

/**
 * 我的身份二维码（院方端个人信息页）：本人查看/下载/复制自己的现场身份码。
 *
 * 与账户管理端 `AccountQrModal` 同一套码与语义，区别只在自助端点
 * （`/auth/my-qrcode`，仅需登录、无需 `sys:user:manage`，目标固定为当前用户）。
 * 打开时优先复用当前有效码，没有才自动签发一张，避免每次查看都换码使已打印工牌失效。
 */
export default function SelfQrPanel({ hq, active, userName, onClose }: SelfQrPanelProps) {
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState<QrData | null>(null);
  const [image, setImage] = useState<string>('');

  const issue = useCallback(async () => {
    setBusy(true);
    try {
      const result = await hq.auth.accountQr.issueMy();
      setData(result);
      message.success('身份码已生成（旧码自动作废）');
    } catch (err) {
      message.error(err instanceof ApiError ? err.friendlyMessage : '生成失败，请重试');
    } finally {
      setBusy(false);
    }
  }, [hq, message]);

  // 进入页签时先取当前有效码：有就展示，没有则自动签发一张
  useEffect(() => {
    if (!active) return undefined;
    let alive = true;
    setLoading(true);
    setData(null);
    setImage('');
    hq.auth.accountQr
      .getMy()
      .then(async (activeQr) => {
        if (!alive) return;
        if (activeQr?.code) {
          setData(activeQr);
          return;
        }
        const result = await hq.auth.accountQr.issueMy().catch(() => null);
        if (alive && result) setData(result);
      })
      .catch(() => undefined)
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [active, hq]);

  const code = data?.code ?? '';
  useEffect(() => {
    if (!code) {
      setImage('');
      return undefined;
    }
    let alive = true;
    void qrDataUrl(code)
      .then((url) => {
        if (alive) setImage(url);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [code]);

  const revoke = async () => {
    setBusy(true);
    try {
      await hq.auth.accountQr.revokeMy();
      setData(null);
      setImage('');
      message.success('身份码已吊销');
    } catch (err) {
      message.error(err instanceof ApiError ? err.friendlyMessage : '吊销失败，请重试');
    } finally {
      setBusy(false);
    }
  };

  const copyCode = async () => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      message.success('身份码内容已复制，可粘贴分享');
    } catch {
      message.warning('浏览器不允许访问剪贴板，请手动选择文本复制');
    }
  };

  const download = () => {
    if (!image) return;
    downloadDataUrl(image, `${safeFileName(`我的身份码_${userName ?? ''}`)}.png`);
  };

  const expireText = formatDisplayTime(data?.expireAt);
  const expired = data?.expireAt ? new Date(data.expireAt).getTime() < Date.now() : false;

  if (loading || busy) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  if (!data || !code) {
    return (
      <>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="App 扫码确认送达与签收"
          description="身份码内含机构、科室、账户与有效期，默认 30 天，可用于送货签收或现场身份确认。"
        />
        <div style={{ textAlign: 'center' }}>
          <Button type="primary" onClick={() => void issue()}>
            生成我的身份码
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="App 扫码确认送达与签收"
        description="二维码内含机构、科室、账户与有效期，默认 30 天。送货人员用 App/PDA 扫码即以本人作为签收人。重新生成会立即作废旧码（已打印的工牌随之失效）。"
      />

      <div style={{ textAlign: 'center', marginBottom: 12 }}>
        {image ? (
          <img
            src={image}
            alt="我的身份码"
            width={240}
            height={240}
            style={{ border: '1px solid #f0f0f0', borderRadius: 8, background: '#fff' }}
          />
        ) : (
          <Spin />
        )}
        <div style={{ marginTop: 8 }}>
          {expired ? <Tag color="red">已过期</Tag> : <Tag color="green">有效</Tag>}
          <Typography.Text type="secondary">有效期至 {expireText}</Typography.Text>
        </div>
      </div>

      <Descriptions column={1} size="small" styles={{ label: { width: 84 } }}>
        <Descriptions.Item label="账户">
          {data.realName ?? '-'}
          {data.userCode ? <Typography.Text type="secondary"> · {data.userCode}</Typography.Text> : null}
        </Descriptions.Item>
        <Descriptions.Item label="机构">{data.orgName ?? '-'}</Descriptions.Item>
        <Descriptions.Item label="科室">{data.deptName ?? '-'}</Descriptions.Item>
        <Descriptions.Item label="扫码次数">
          {'scanCount' in data ? (data.scanCount ?? 0) : 0}
        </Descriptions.Item>
      </Descriptions>

      <Space wrap style={{ marginTop: 16 }}>
        <Button type="primary" icon={<DownloadOutlined />} onClick={download}>
          下载图片
        </Button>
        <Button icon={<CopyOutlined />} onClick={() => void copyCode()}>
          复制内容
        </Button>
        <Button icon={<ReloadOutlined />} onClick={() => void issue()}>
          重新生成
        </Button>
        <Button danger icon={<StopOutlined />} onClick={() => void revoke()}>
          吊销
        </Button>
        <Button onClick={onClose}>关闭</Button>
      </Space>
    </>
  );
}
